import React from 'react';
import PropTypes from 'prop-types';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { X } from 'lucide-react';

export function ProcessorStep2Operations({
  formData,
  handleInputChange,
  cacaoVarieties,
  newCacaoVariety,
  setNewCacaoVariety,
  addCacaoVariety,
  removeCacaoVariety,
}) {
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-blue-900 border-b-2 border-blue-200 pb-2">
        Processing Operations
      </h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="quantityOfCacaoBeansPerProcessingCycle">Cacao Beans per Processing Cycle (kg) *</Label>
          <Input
            id="quantityOfCacaoBeansPerProcessingCycle"
            type="number"
            value={formData.quantityOfCacaoBeansPerProcessingCycle}
            onChange={(e) => handleInputChange('quantityOfCacaoBeansPerProcessingCycle', e.target.value)}
            placeholder="e.g., 500"
            min="0"
            step="0.01"
            required
          />
        </div>
        <div>
          <Label htmlFor="frequencyOfProcessing">Frequency of Processing *</Label>
          <Select value={formData.frequencyOfProcessing} onValueChange={(value) => handleInputChange('frequencyOfProcessing', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Daily">Daily</SelectItem>
              <SelectItem value="Weekly">Weekly</SelectItem>
              <SelectItem value="Twice A Week">Twice A Week</SelectItem>
              <SelectItem value="Monthly">Monthly</SelectItem>
              <SelectItem value="Seasonal">Seasonal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="preferredVarietyOfCacao">Preferred Variety of Cacao</Label>
          <Select value={formData.preferredVarietyOfCacao} onValueChange={(value) => handleInputChange('preferredVarietyOfCacao', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select variety" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Trinitario">Trinitario</SelectItem>
              <SelectItem value="Criollo">Criollo</SelectItem>
              <SelectItem value="Forastero">Forastero</SelectItem>
              <SelectItem value="Mixed">Mixed Varieties</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Varieties of Cacao Processed</Label>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={newCacaoVariety}
                onChange={(e) => setNewCacaoVariety(e.target.value)}
                placeholder="Enter cacao variety (e.g., Trinitario, Criollo)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    addCacaoVariety();
                  }
                }}
              />
              <Button type="button" onClick={addCacaoVariety} variant="outline">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {cacaoVarieties.map((variety, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {variety}
                  <button
                    type="button"
                    onClick={() => removeCacaoVariety(variety)}
                    className="ml-1 hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <div>
          <Label htmlFor="sourceOfCacao">Source of Cacao</Label>
          <Input
            id="sourceOfCacao"
            value={formData.sourceOfCacao}
            onChange={(e) => handleInputChange('sourceOfCacao', e.target.value)}
            placeholder="e.g., Local Farmers, Cooperatives"
          />
        </div>
        <div>
          <Label htmlFor="frequencyOfDelivery">Frequency of Delivery</Label>
          <Select value={formData.frequencyOfDelivery} onValueChange={(value) => handleInputChange('frequencyOfDelivery', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select delivery frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Daily">Daily</SelectItem>
              <SelectItem value="Weekly">Weekly</SelectItem>
              <SelectItem value="Twice A Week">Twice A Week</SelectItem>
              <SelectItem value="Monthly">Monthly</SelectItem>
              <SelectItem value="Quarterly">Quarterly</SelectItem>
              <SelectItem value="As needed">As needed</SelectItem>
              <SelectItem value="Seasonal">Seasonal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="quantityOfCacaoDelivered">Quantity of Cacao Delivered (kg)</Label>
          <Input
            id="quantityOfCacaoDelivered"
            type="number"
            value={formData.quantityOfCacaoDelivered}
            onChange={(e) => handleInputChange('quantityOfCacaoDelivered', e.target.value)}
            placeholder="e.g., 1000"
            min="0"
            step="0.01"
          />
        </div>
        <div>
          <Label htmlFor="formTypeOfCacaoBought">Form/Type of Cacao Bought</Label>
          <Input
            id="formTypeOfCacaoBought"
            value={formData.formTypeOfCacaoBought}
            onChange={(e) => handleInputChange('formTypeOfCacaoBought', e.target.value)}
            placeholder="e.g., Fresh Pods, Wet Beans, Dried Beans, Fermented Beans"
          />
        </div>
        <div>
          <Label htmlFor="formType">Cacao Form Type</Label>
          <Input
            id="formType"
            value={formData.formType}
            onChange={(e) => handleInputChange('formType', e.target.value)}
            placeholder="e.g., Wet, Dry, Fermented"
          />
        </div>
        <div>
          <Label htmlFor="producersToProcessors">Producers to Processors</Label>
          <Input
            id="producersToProcessors"
            value={formData.producersToProcessors}
            onChange={(e) => handleInputChange('producersToProcessors', e.target.value)}
            placeholder="e.g."
          />
        </div>
        <div>
          <Label htmlFor="localTradersToProcessors">Local Traders to Processors</Label>
          <Input
            id="localTradersToProcessors"
            value={formData.localTradersToProcessors}
            onChange={(e) => handleInputChange('localTradersToProcessors', e.target.value)}
            placeholder="e.g."
          />
        </div>
      </div>
    </div>
  );
}

ProcessorStep2Operations.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  cacaoVarieties: PropTypes.array,
  newCacaoVariety: PropTypes.string,
  setNewCacaoVariety: PropTypes.func,
  addCacaoVariety: PropTypes.func,
  removeCacaoVariety: PropTypes.func,
};
